import { apiRequest } from "./apiClient";
import { rt } from "../i18n/runtime";
import { currentTimeZonePayload } from "../hooks/useUserTimezone";
import { getRuntimeVersionLabel } from "./runtimeAppInfo";

const VAPID_PUBLIC_KEY =
  "BIRYnfpLjMmnsT172TNiVip7mZnd56_q4LIArQlPe38ZDJVnA_7ZoLJExEjnYJp52vcesTacWzlbCHkyQtkMOMc".trim();

function urlBase64ToUint8Array(base64String) {
  const cleanBase64 = base64String.trim().replace(/\s/g, "");
  const padding = "=".repeat((4 - (cleanBase64.length % 4)) % 4);
  const base64 = (cleanBase64 + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  if (outputArray.length !== 65) {
    throw new Error(
      rt("service.error.vapidInvalid", { length: outputArray.length })
    );
  }

  return outputArray;
}

/**
 * Endpoint URL'den tutarlı bir deviceId üret.
 * Aynı abonelik her zaman aynı backend push_devices kaydını günceller.
 */
function getDeviceIdFromEndpoint(endpoint) {
  // Endpoint'in son 40 karakterini al — yeterince benzersiz
  const raw = endpoint.replace(/[^a-zA-Z0-9]/g, "").slice(-40);
  return `wp_${raw}`;
}

function isStandalonePwa() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIosDevice() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

const WEB_PUSH_VAPID_STORAGE_KEY = "familyPlannerWebPushVapidPublicKey";

function getStoredVapidKey() {
  try { return localStorage.getItem(WEB_PUSH_VAPID_STORAGE_KEY); }
  catch { return null; }
}

function setStoredVapidKey(value) {
  try { localStorage.setItem(WEB_PUSH_VAPID_STORAGE_KEY, value); }
  catch {}
}

async function getFreshSubscription(registration) {
  const existing = await registration.pushManager.getSubscription();
  const storedKey = getStoredVapidKey();

  // iOS/Safari bazı sürümlerde subscription.options.applicationServerKey güvenilir dönmeyebilir.
  // Bu yüzden kendi localStorage VAPID işaretimizi kullanıp eski aboneliği yeniliyoruz.
  if (existing && storedKey === VAPID_PUBLIC_KEY) return existing;

  if (existing) {
    try { await existing.unsubscribe(); } catch {}
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
  });
  setStoredVapidKey(VAPID_PUBLIC_KEY);
  return subscription;
}

async function saveSubscriptionToBackend({ user, familyId, subscription, permission }) {
  const deviceId = getDeviceIdFromEndpoint(subscription.endpoint);
  const uid = user.firebase_uid || user.uid;
  const appVersion = await getRuntimeVersionLabel();

  const data = await apiRequest("/notifications/push-device", {
    method: "POST",
    body: JSON.stringify({
      userUid: uid,
      familyId: familyId || null,
      deviceId,
      platform: "web",
      pushType: "web-push",
      token: subscription.toJSON(),
      permission,
      standalone: isStandalonePwa(),
      userAgent: navigator.userAgent,
      locale: getAppLocale(),
      appVersion,
      ...currentTimeZonePayload(),
      timezoneSource: "push_registration",
    }),
  });

  return data;
}

function getAppLocale() {
  try {
    return localStorage.getItem("appLanguage") === "en" ? "en" : "tr";
  } catch {
    return "tr";
  }
}

function getUserFamilyIds(user, activeFamilyId) {
  const familyIds = new Set();
  if (activeFamilyId) familyIds.add(activeFamilyId);
  if (user?.familyId) familyIds.add(user.familyId);
  if (user?.aktivFamilyId) familyIds.add(user.aktivFamilyId);
  if (Array.isArray(user?.families)) {
    user.families.forEach((f) => {
      if (typeof f === "string") familyIds.add(f);
      else if (f?.familyId) familyIds.add(f.familyId);
      else if (f?.id) familyIds.add(f.id);
    });
  }
  return [...familyIds].filter(Boolean);
}

/* Kullanıcının bilinen tüm ailelerine aboneliği backend'e kaydet. */
async function saveSubscriptionToAllKnownFamilies({ user, familyId, subscription, permission }) {
  const familyIds = getUserFamilyIds(user, familyId);
  if (familyIds.length === 0) {
    await saveSubscriptionToBackend({ user, familyId: null, subscription, permission });
    return subscription;
  }

  await Promise.all(
    familyIds.map((fid) =>
      saveSubscriptionToBackend({ user, familyId: fid, subscription, permission })
    )
  );

  return subscription;
}

export async function requestAndSaveNotificationToken({
  user,
  familyId,
  silent = false,
}) {
  if (!user?.uid || !familyId) {
    if (silent) return null;
    throw new Error(rt("service.error.userFamilyMissing"));
  }

  if (!window.isSecureContext) {
    if (silent) return null;
    throw new Error(rt("service.error.httpsRequired"));
  }

  if (!("serviceWorker" in navigator)) {
    if (silent) return null;
    throw new Error(rt("service.error.noServiceWorker"));
  }

  if (!("PushManager" in window)) {
    if (silent) return null;
    throw new Error(rt("service.error.noWebPush"));
  }

  if (!("Notification" in window)) {
    if (silent) return null;
    throw new Error(rt("service.error.noNotification"));
  }

  if (isIosDevice() && !isStandalonePwa()) {
    if (silent) return null;
    throw new Error(rt("service.error.iosStandalone"));
  }

  let permission = Notification.permission;

  if (silent && permission !== "granted") {
    return null;
  }

  if (permission === "default") {
    permission = await Notification.requestPermission();
  }

  if (permission !== "granted") {
    if (silent) return null;
    throw new Error(rt("service.error.permissionDenied"));
  }

  const registration = await navigator.serviceWorker.register("/web-push-sw.js");

  const subscription = await getFreshSubscription(registration);

  // Kayıt merkezi backend push_devices tablosu.
  return saveSubscriptionToAllKnownFamilies({ user, familyId, subscription, permission });
}
